import { Grid } from "@material-ui/core";
import MultipleSelect from "@saleor/changeOrderExport/components/OrderStatus";
import DynamicMultipleSelect from "@saleor/components/DynamicMultipleSelect";
import MyTypography from "@saleor/components/MyTypography";
import {
  ScgpUserQuery,
  useFilterUserManagementSaleGroupBySaleOrgQuery,
  useFilterUserManagementSaleOfficeBySaleOrgQuery,
  useFilterUserManagementSaleOrgByBuQuery,
  useGetBusinessUnitQuery,
  useGetDistributionChannelQuery,
  // useGetListSaleGroupsQuery,
  // useGetListSaleOrganizationsQuery,
  // useGetListSalesOfficesQuery,
  useGetListScgDivisionQuery
} from "@saleor/graphql";
import { UserDetailMode } from "@saleor/userManagement/constants";
import {
  mapEdgesToChoices,
  mapEdgesToItems,
  mapNodeToChoice
} from "@saleor/utils/maps";
import React, { useMemo } from "react";
import { useIntl } from "react-intl";

import useStyles from "./styles";

interface FormOtherAgencyProps {
  formik?: any;
  groupId?: string;
  user?: ScgpUserQuery;
  mode?: UserDetailMode;
}

const FormOtherAgency: React.FC<FormOtherAgencyProps> = ({
  formik,
  user,
  mode
}) => {
  const classes = useStyles();
  const intl = useIntl();

  const { data: dataBuChoice } = useGetBusinessUnitQuery({
    displayLoader: true
  });

  const businessUnitChoices = useMemo(
    () => mapEdgesToChoices(dataBuChoice?.businessUnits),
    [dataBuChoice]
  );

  const {
    data: dataSaleOrganization
  } = useFilterUserManagementSaleOrgByBuQuery({
    displayLoader: true,
    variables: {
      filter: {
        businessUnit:
          businessUnitChoices && businessUnitChoices.length
            ? formik.values.businessUnits
                .map(
                  e =>
                    (businessUnitChoices || [])?.find(u => u.value === e)?.label
                )
                ?.filter(e => e)
            : []
      },
      first: 100
    }
  });

  const { data: dataDistributionChannel } = useGetDistributionChannelQuery({
    displayLoader: true
  });

  const { data: dataDivision } = useGetListScgDivisionQuery({
    displayLoader: true
  });

  const saleOrganizationChoices = useMemo(
    () =>
      dataSaleOrganization?.filterUserManagementSaleOrgByBu?.edges?.map(
        ({ node }) => ({
          label: `${node.code} - ${node.shortName}`,
          value: node.id
        })
      ) || [],
    [dataSaleOrganization]
  );

  const {
    data: dataSaleOffice
  } = useFilterUserManagementSaleOfficeBySaleOrgQuery({
    displayLoader: true,
    variables: {
      filter: {
        salesOrganization:
          saleOrganizationChoices && saleOrganizationChoices.length
            ? formik.values.saleOrganizations
                .map(
                  e =>
                    (
                      mapEdgesToItems(
                        dataSaleOrganization.filterUserManagementSaleOrgByBu
                      ) || []
                    )?.find(u => u.id === e)?.code
                )
                ?.filter(e => e)
            : []
      },
      first: 100
    }
  });

  const {
    data: dataSaleGroup
  } = useFilterUserManagementSaleGroupBySaleOrgQuery({
    displayLoader: true,
    variables: {
      filter: {
        salesOrganization:
          saleOrganizationChoices && saleOrganizationChoices.length
            ? (formik.values.saleOrganizations || [])
                .map(
                  e =>
                    (
                      mapEdgesToItems(
                        dataSaleOrganization.filterUserManagementSaleOrgByBu
                      ) || []
                    )?.find(u => u.id === e)?.code
                )
                ?.filter(e => e)
            : []
      },
      first: 1000
    }
  });

  const saleGroupChoices = useMemo(
    () =>
      dataSaleGroup?.filterUserManagementSaleGroupBySaleOrg?.edges?.map(
        ({ node }) => ({
          label: node.displayText,
          value: node.id
        })
      ) || [],
    [dataSaleGroup]
  );

  const distributionChannelChoices = useMemo(
    () => mapNodeToChoice(dataDistributionChannel?.distributionChannels),
    [dataDistributionChannel]
  );

  const divisionChoices = useMemo(
    () => mapNodeToChoice(dataDivision?.scgDivisions),
    [dataDivision]
  );

  const saleOfficeChoices = useMemo(
    () =>
      dataSaleOffice?.filterUserManagementSaleOfficeBySaleOrg?.edges?.map(
        ({ node }) => ({
          label: node.displayText,
          value: node.id
        })
      ) || [],
    [dataSaleOffice]
  );

  const getErrorText = field =>
    mode !== UserDetailMode.VIEW ? (
      <div className={classes.textError}>{formik.errors[field]}</div>
    ) : (
      ""
    );

  const getMultiValueCodeNameText = field =>
    user?.scgpUser?.extendData?.[field]
      ?.map(x => `${x?.code} ${x?.name}`)
      ?.join(", ") || "-";

  return (
    <>
      {mode !== UserDetailMode.VIEW ? (
        <Grid item md={6}>
          <MyTypography variant={"subtitle3"} className={classes.inputLabel}>
            {intl.formatMessage({ defaultMessage: "BU" })}
            <span className={classes.required}>*</span>
          </MyTypography>
          {businessUnitChoices && businessUnitChoices.length > 0 && (
            <MultipleSelect
              showChipDeleteButton={false}
              showCircularIcon={false}
              choices={businessUnitChoices}
              name="businessUnits"
              placeholder="เลือก BU"
              numberItemDisplay={2}
              value={formik.values.businessUnits}
              onChange={data => {
                formik.handleChange(data);
                if (!data?.target?.value || !data?.target?.value?.length) {
                  formik.setFieldValue("saleGroups", []);
                  formik.setFieldValue("saleOrganizations", []);
                  formik.setFieldValue("saleOffices", []);
                }
              }}
              error={!!formik.errors.businessUnits}
            />
          )}
          {getErrorText("businessUnits")}
        </Grid>
      ) : (
        <Grid item md={6}>
          <MyTypography variant={"subtitle3"} className={classes.inputLabel}>
            {intl.formatMessage({ defaultMessage: "BU" })}
          </MyTypography>
          <MyTypography variant={"body3"}>
            {user?.scgpUser?.extendData?.scgpBus
              ?.map(x => x?.name)
              ?.join(", ") || "-"}
          </MyTypography>
        </Grid>
      )}
      {mode === UserDetailMode.EDIT || mode === UserDetailMode.CREATE ? (
        <Grid item md={6}>
          <MyTypography variant={"subtitle3"} className={classes.inputLabel}>
            {intl.formatMessage({ defaultMessage: "Sales Organization" })}
            <span className={classes.required}>*</span>
          </MyTypography>
          {saleOrganizationChoices && saleOrganizationChoices.length > 0 && (
            <DynamicMultipleSelect
              showChipDeleteButton={false}
              showCircularIcon={false}
              choices={saleOrganizationChoices}
              name="saleOrganizations"
              placeholder="เลือก Sales Organization"
              disabled={
                !formik.values.businessUnits ||
                !formik.values.businessUnits?.length
              }
              numberItemDisplay={2}
              value={formik.values.saleOrganizations}
              onChange={data => {
                formik.handleChange(data);
                if (!data.target.value || !data.target.value.length) {
                  formik.setFieldValue("saleOffices", []);
                  formik.setFieldValue("saleGroups", []);
                  formik.setFieldValue("saleOffices", []);
                }
              }}
              error={!!formik.errors.saleOrganizations}
            />
          )}
          {getErrorText("saleOrganizations")}
        </Grid>
      ) : (
        <Grid item md={6}>
          <MyTypography variant={"subtitle3"} className={classes.inputLabel}>
            {intl.formatMessage({ defaultMessage: "Sales Organization" })}
          </MyTypography>
          <MyTypography variant={"body3"}>
            {getMultiValueCodeNameText("scgpSalesOrganizations")}
          </MyTypography>
        </Grid>
      )}
      {mode === UserDetailMode.VIEW && (
        <>
          <Grid item md={6}>
            <MyTypography variant={"subtitle3"} className={classes.inputLabel}>
              {intl.formatMessage({ defaultMessage: "Sale Group" })}
            </MyTypography>
            <MyTypography variant={"body3"}>
              {getMultiValueCodeNameText("scgpSalesGroups")}
            </MyTypography>
          </Grid>
          <Grid item md={6}>
            <MyTypography variant={"subtitle3"} className={classes.inputLabel}>
              {intl.formatMessage({ defaultMessage: "Distribution Channel" })}
            </MyTypography>
            <MyTypography variant={"body3"}>
              {getMultiValueCodeNameText("scgpDistributionChannels")}
            </MyTypography>
          </Grid>
          <Grid item md={6}>
            <MyTypography variant={"subtitle3"} className={classes.inputLabel}>
              {intl.formatMessage({ defaultMessage: "Division" })}
            </MyTypography>
            <MyTypography variant={"body3"}>
              {getMultiValueCodeNameText("scgpDivisions")}
            </MyTypography>
          </Grid>
          <Grid item md={6}>
            <MyTypography variant={"subtitle3"} className={classes.inputLabel}>
              {intl.formatMessage({ defaultMessage: "Sale Office" })}
            </MyTypography>
            <MyTypography variant={"body3"}>
              {getMultiValueCodeNameText("scgpSalesOffices")}
            </MyTypography>
          </Grid>
        </>
      )}
      {mode !== UserDetailMode.VIEW && (
        <>
          <Grid item md={6}>
            <MyTypography variant={"subtitle3"} className={classes.inputLabel}>
              {intl.formatMessage({ defaultMessage: "Sale Group" })}
              <span className={classes.required}>*</span>
            </MyTypography>
            {saleGroupChoices && saleGroupChoices.length ? (
              <DynamicMultipleSelect
                showChipDeleteButton={false}
                showCircularIcon={false}
                choices={saleGroupChoices || []}
                name="saleGroups"
                placeholder="เลือก Sale Group"
                numberItemDisplay={2}
                disabled={
                  !formik.values.saleOrganizations ||
                  !formik.values.saleOrganizations?.length
                }
                value={formik.values.saleGroups}
                onChange={formik.handleChange}
                error={!!formik.errors.saleGroups}
              />
            ) : (
              <MultipleSelect
                showChipDeleteButton={false}
                showCircularIcon={false}
                choices={[]}
                name="saleGroups"
                placeholder="เลือก Sale Group"
                numberItemDisplay={2}
                disabled={
                  !formik.values.saleOrganizations ||
                  !formik.values.saleOrganizations?.length
                }
                value={formik.values.saleGroups}
                onChange={formik.handleChange}
                error={!!formik.errors.saleGroups}
              />
            )}
            {getErrorText("saleGroups")}
          </Grid>
          <Grid item md={6}>
            <MyTypography variant={"subtitle3"} className={classes.inputLabel}>
              {intl.formatMessage({ defaultMessage: "Distribution Channel" })}
              <span className={classes.required}>*</span>
            </MyTypography>
            <MultipleSelect
              showChipDeleteButton={false}
              showCircularIcon={false}
              choices={distributionChannelChoices || []}
              name="distributionChannels"
              placeholder="เลือก Distribution Channel"
              numberItemDisplay={2}
              value={formik.values.distributionChannels}
              onChange={formik.handleChange}
              error={!!formik.errors.distributionChannels}
            />
            {getErrorText("distributionChannels")}
          </Grid>
          <Grid item md={6}>
            <MyTypography variant={"subtitle3"} className={classes.inputLabel}>
              {intl.formatMessage({ defaultMessage: "Division" })}
              <span className={classes.required}>*</span>
            </MyTypography>
            <MultipleSelect
              showChipDeleteButton={false}
              showCircularIcon={false}
              choices={divisionChoices || []}
              name="divisions"
              placeholder="เลือก Division"
              numberItemDisplay={2}
              value={formik.values.divisions}
              onChange={formik.handleChange}
              error={!!formik.errors.divisions}
            />
            {getErrorText("divisions")}
          </Grid>
          <Grid item md={6}>
            <MyTypography variant={"subtitle3"} className={classes.inputLabel}>
              {intl.formatMessage({ defaultMessage: "Sale Office" })}
              <span className={classes.required}>*</span>
            </MyTypography>
            {saleOfficeChoices && saleOfficeChoices.length ? (
              <DynamicMultipleSelect
                showChipDeleteButton={false}
                showCircularIcon={false}
                choices={saleOfficeChoices || []}
                name="saleOffices"
                placeholder="เลือก Sale Office"
                numberItemDisplay={2}
                disabled={
                  !formik.values.saleOrganizations ||
                  !formik.values.saleOrganizations?.length
                }
                value={formik.values.saleOffices}
                onChange={formik.handleChange}
                error={!!formik.errors.saleOffices}
              />
            ) : (
              <MultipleSelect
                showChipDeleteButton={false}
                showCircularIcon={false}
                choices={[]}
                name="saleOffices"
                placeholder="เลือก Sale Office"
                numberItemDisplay={2}
                disabled={
                  !formik.values.saleOrganizations ||
                  !formik.values.saleOrganizations?.length
                }
                value={formik.values.saleOffices}
                onChange={formik.handleChange}
                error={!!formik.errors.saleOffices}
              />
            )}
            {getErrorText("saleOffices")}
          </Grid>
        </>
      )}
    </>
  );
};

export default FormOtherAgency;
